import { useBillingData } from "../api/queries";
import { env } from "@/shared/config/env";

export interface BetaVendorState {
  /** True while the current-plan query is still in flight. */
  isLoading: boolean;
  /** True only for the beta cohort — see the fingerprint note below. */
  isBeta: boolean;
}

/**
 * Detects the beta cohort: vendors who onboarded while
 * VITE_BETA_ONBOARDING_ENABLED was on are enrolled on the STARTER plan with
 * an open-ended TRIAL — so `/subscriptions/my` reports `plan.code "STARTER"`
 * + `status "TRIALING"`. No normal flow produces that pair (regular trials
 * use the FREE_TRIAL plan; a paid Starter is ACTIVE), and it dissolves on
 * its own when the beta ends and those stores convert.
 *
 * This is the SINGLE source of truth for beta detection — if the backend
 * later exposes an explicit `isBeta` flag, swap the fingerprint here only.
 *
 * Fails open: while loading, on error, or with no subscription data the
 * vendor is treated as NOT beta, so billing never disappears for normal
 * vendors because of a flaky request.
 */
export function useIsBetaVendor(): BetaVendorState {
  const { currentPlan, isLoading } = useBillingData();

  // Staging runs the beta programme for EVERYONE: every vendor there is
  // beta regardless of subscription — the fingerprint below is unreliable
  // on stg (its backend reports trial status inconsistently) and must not
  // decide anything. isLoading is false because the answer doesn't depend
  // on the plan query. dev/prod builds are untouched.
  if (env.APP_ENV === "stg") {
    return { isBeta: true, isLoading: false };
  }
  const sub = (
    currentPlan as
      | { data?: { plan?: { code?: string }; status?: string } }
      | undefined
  )?.data;

  // Normalise before comparing: environments/BE versions have reported the
  // trial status as both "TRIAL" (onboarding write shape) and "TRIALING"
  // (subscription read shape), with inconsistent casing. The fingerprint
  // must not silently fail on a serialization variant.
  const code = (sub?.plan?.code ?? "").toUpperCase();
  const status = (sub?.status ?? "").toUpperCase();
  const isBeta = !isLoading && code === "STARTER" && status.startsWith("TRIAL");

  return { isBeta, isLoading };
}
